import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAssessmentQuestions, submitAssessment, getAssessmentHistory } from '../services/api';
import { 
  Shield, ArrowLeft, ArrowRight, CheckCircle, AlertCircle, 
  History, ChevronRight
} from 'lucide-react';

export default function AssessmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [questionsRes, historyRes] = await Promise.all([
          getAssessmentQuestions(),
          getAssessmentHistory()
        ]);
        setQuestions(questionsRes.data.questions);
        setHistory(historyRes.data.assessments);
      } catch (error) {
        console.error('Failed to fetch assessment data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [questions[currentQuestion].id]: value });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));
      const response = await submitAssessment(formattedAnswers);
      setResult(response.data);
    } catch (error) {
      console.error('Failed to submit assessment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const maturityLabels = {
    1: { label: 'Stagnant', color: '#DC2626', bg: 'bg-red-50' },
    2: { label: 'Searching', color: '#D97706', bg: 'bg-amber-50' },
    3: { label: 'Striving', color: '#F59E0B', bg: 'bg-yellow-50' },
    4: { label: 'Stretching', color: '#059669', bg: 'bg-green-50' },
    5: { label: 'Sustaining', color: '#10B981', bg: 'bg-emerald-50' },
  };

  const answerLabels = [
    { value: 1, label: 'Strongly Disagree', color: '#DC2626' },
    { value: 2, label: 'Disagree', color: '#D97706' },
    { value: 3, label: 'Neutral', color: '#64748B' },
    { value: 4, label: 'Agree', color: '#059669' },
    { value: 5, label: 'Strongly Agree', color: '#10B981' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-text-muted">Loading assessment...</div>
      </div>
    );
  }

  if (result) {
    const maturity = maturityLabels[result.maturity_level];
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-surface border-b border-border px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-md transition-all">
              <ArrowLeft className="w-5 h-5 text-text-muted" />
            </Link>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              <span className="font-outfit text-xl font-bold text-primary">Assessment Complete</span>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto p-6">
          <div className="bg-surface border border-border rounded-lg p-8 text-center animate-in" data-testid="assessment-result">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-risk-safe" />
            <h2 className="font-outfit text-3xl font-bold text-primary mb-2">Assessment Complete!</h2>
            <p className="text-text-muted mb-8">Here's your district's risk intelligence maturity level</p>

            <div className={`inline-block px-8 py-6 rounded-lg ${maturity.bg} mb-8`}>
              <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">Maturity Level</p>
              <p className="text-5xl font-outfit font-bold mb-2" style={{ color: maturity.color }}>
                Level {result.maturity_level}
              </p>
              <p className="text-xl font-semibold text-primary">{maturity.label}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {Object.entries(result.scores).map(([category, score]) => (
                <div key={category} className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-text-muted font-semibold mb-1">
                    {category}
                  </p>
                  <p className="text-2xl font-outfit font-bold text-primary">{score.toFixed(1)}</p>
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-4">
              <Link 
                to="/training"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-primary-hover transition-all"
              >
                Start Training
                <ChevronRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/dashboard"
                className="inline-flex items-center gap-2 bg-slate-100 text-primary px-6 py-3 rounded-md font-medium hover:bg-slate-200 transition-all"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const allAnswered = questions.every(q => answers[q.id] !== undefined);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-surface border-b border-border px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-md transition-all">
              <ArrowLeft className="w-5 h-5 text-text-muted" />
            </Link>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              <span className="font-outfit text-xl font-bold text-primary">Maturity Assessment</span>
            </div>
          </div>
          <button 
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-text-muted hover:text-primary transition-all"
            data-testid="toggle-history"
          >
            <History className="w-5 h-5" />
            <span className="hidden md:inline">History</span>
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        {showHistory ? (
          <div className="bg-surface border border-border rounded-lg p-6 animate-in" data-testid="assessment-history">
            <h2 className="font-outfit text-xl font-semibold text-primary mb-4">Assessment History</h2>
            {history.length > 0 ? (
              <div className="space-y-4">
                {history.map((assessment, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-primary">Level {assessment.maturity_level} - {assessment.maturity_label}</p>
                      <p className="text-sm text-text-muted">{new Date(assessment.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-outfit font-bold text-primary">{assessment.overall_score.toFixed(1)}</p>
                      <p className="text-xs text-text-muted">Overall Score</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-text-muted text-center py-8">No previous assessments found</p>
            )}
            <button 
              onClick={() => setShowHistory(false)}
              className="mt-4 w-full bg-primary text-white py-3 rounded-md font-medium hover:bg-primary-hover transition-all"
            >
              Start New Assessment
            </button>
          </div>
        ) : (
          <>
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-text-muted mb-2">
                <span>Question {currentQuestion + 1} of {questions.length}</span>
                <span>{Math.round(progress)}% complete</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-accent-blue transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Question card */}
            <div className="bg-surface border border-border rounded-lg p-8 animate-in" data-testid="question-card">
              <div className="mb-2">
                <span className="inline-block px-3 py-1 bg-slate-100 text-text-muted text-xs uppercase tracking-wider rounded-full font-semibold">
                  {question.category}
                </span>
                <span className="ml-2 text-sm text-text-muted">{question.skill}</span>
              </div>
              
              <h2 className="font-outfit text-2xl font-semibold text-primary mb-8">
                {question.text}
              </h2>

              <div className="space-y-3">
                {answerLabels.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(option.value)}
                    className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                      answers[question.id] === option.value
                        ? 'border-accent-blue bg-blue-50'
                        : 'border-border hover:border-slate-300'
                    }`}
                    data-testid={`answer-${option.value}`}
                  >
                    <div 
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        answers[question.id] === option.value
                          ? 'border-accent-blue bg-accent-blue'
                          : 'border-slate-300'
                      }`}
                    >
                      {answers[question.id] === option.value && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span className="font-medium text-primary">{option.label}</span>
                    <span className="ml-auto text-text-muted">{option.value}/5</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="flex items-center gap-2 px-6 py-3 rounded-md font-medium text-primary bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="prev-question"
              >
                <ArrowLeft className="w-5 h-5" />
                Previous
              </button>

              {currentQuestion === questions.length - 1 ? (
                <button
                  onClick={handleSubmit}
                  disabled={!allAnswered || submitting}
                  className="flex items-center gap-2 px-6 py-3 rounded-md font-medium text-white bg-risk-safe hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="submit-assessment"
                >
                  {submitting ? 'Submitting...' : 'Submit Assessment'}
                  <CheckCircle className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={answers[question.id] === undefined}
                  className="flex items-center gap-2 px-6 py-3 rounded-md font-medium text-white bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="next-question"
                >
                  Next
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Question indicators */}
            <div className="flex justify-center gap-2 mt-8">
              {questions.map((q, index) => (
                <button
                  key={q.id}
                  onClick={() => setCurrentQuestion(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentQuestion
                      ? 'bg-accent-blue scale-125'
                      : answers[q.id] !== undefined
                      ? 'bg-risk-safe'
                      : 'bg-slate-300'
                  }`}
                  data-testid={`question-indicator-${index}`}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
